/**
 * Tipos del dominio compartidos entre la app móvil/web, el portal y el backend.
 * Nomenclatura en castellano: sigue las entidades de la especificación v2.1.
 */

export const SCHEMA_VERSION = '1.3';

export type ResultadoControl =
  | 'REALIZADA'
  | 'PACIENTE_AUSENTE'
  | 'RECHAZA_CONTROL'
  | 'DOMICILIO_ERRONEO'
  | 'MEDICO_IMPOSIBILITADO';

export type Deambulacion = 'SI' | 'NO' | 'CON_ASISTENCIA';

/** Dictamen de aptitud laboral del médico de control (ADR-0009). */
export type DictamenAptitud = 'APTO_PARA_TRABAJAR' | 'NO_APTO_TEMPORAL';

/**
 * Aparato o sistema afectado, cuando el trabajador no está apto. Clasificación
 * gruesa (no un diagnóstico CIE-10) — es dato laboral, no queda detrás del
 * secreto profesional: lo ve tanto RRHH como Servicio Médico.
 */
export type AparatoSistema =
  | 'GENERAL_INESPECIFICO'
  | 'HEMATOLOGICO'
  | 'DIGESTIVO'
  | 'OFTALMOLOGICO'
  | 'OIDO'
  | 'CARDIOVASCULAR'
  | 'OSTEOMIOARTICULAR'
  | 'NEUROLOGICO'
  | 'PSICOLOGICO_MENTAL'
  | 'RESPIRATORIO'
  | 'PIEL_DERMATOLOGIA'
  | 'ENDOCRINO_METABOLICO'
  | 'URINARIO'
  | 'EMBARAZO_PARTO'
  | 'GENITAL_FEMENINO'
  | 'GENITAL_MASCULINO';

export const APARATOS_SISTEMA: readonly AparatoSistema[] = [
  'GENERAL_INESPECIFICO',
  'HEMATOLOGICO',
  'DIGESTIVO',
  'OFTALMOLOGICO',
  'OIDO',
  'CARDIOVASCULAR',
  'OSTEOMIOARTICULAR',
  'NEUROLOGICO',
  'PSICOLOGICO_MENTAL',
  'RESPIRATORIO',
  'PIEL_DERMATOLOGIA',
  'ENDOCRINO_METABOLICO',
  'URINARIO',
  'EMBARAZO_PARTO',
  'GENITAL_FEMENINO',
  'GENITAL_MASCULINO',
];

export const ETIQUETA_APARATO_SISTEMA: Record<AparatoSistema, string> = {
  GENERAL_INESPECIFICO: 'General e inespecífico',
  HEMATOLOGICO: 'Hematológico',
  DIGESTIVO: 'Digestivo',
  OFTALMOLOGICO: 'Oftalmológico',
  OIDO: 'Oído',
  CARDIOVASCULAR: 'Cardiovascular',
  OSTEOMIOARTICULAR: 'OstioMioArticular',
  NEUROLOGICO: 'Neurológico',
  PSICOLOGICO_MENTAL: 'Psicológico/Mental',
  RESPIRATORIO: 'Respiratorio',
  PIEL_DERMATOLOGIA: 'Piel (Dermatología)',
  ENDOCRINO_METABOLICO: 'Endocrino, metabólico',
  URINARIO: 'Urinario',
  EMBARAZO_PARTO: 'Embarazo, parto',
  GENITAL_FEMENINO: 'Aparato genital femenino',
  GENITAL_MASCULINO: 'Aparato genital masculino',
};

/**
 * Id de un tramo de control (modalidad/distancia — "Domicilio en la ciudad",
 * "Domicilio 30-60km", "Consultorio", etc.). Ya NO es un enum fijo de 3
 * valores: el Operador administra sus propios tramos desde el panel
 * (`TramoControlDTO`, `/api/operador/tramos`) — este alias solo documenta
 * la intención en las firmas que lo usan.
 */
export type TipoControl = string;

/** Tramo de control administrado por el Operador (ver TipoControl). */
export interface TramoControlDTO {
  id: string;
  nombre: string;
  orden: number;
  activo: boolean;
}

export type VersionDocumento = 'CLINICA' | 'LABORAL';

/** Estado de la firma digital remota del profesional (por operación). */
export type EstadoFirmaProfesional =
  | 'PENDIENTE_AUTORIZACION'
  | 'FIRMADA'
  | 'RECHAZADA'
  | 'EXPIRADA'
  | 'FALLIDA'
  | 'BLOQUEADA_REFEPS';

export type EstadoConstancia =
  | 'GENERADA'
  | 'FIRMA_PENDIENTE'
  | 'FIRMADA'
  | 'FIRMA_BLOQUEADA'
  | 'FALLIDA';

export type EstadoOrden = 'ASIGNADA' | 'EN_CURSO' | 'COMPLETADA' | 'FALLIDA';

export type RolPortal = 'RRHH' | 'SERVICIO_MEDICO';

export type TipoAdjunto =
  | 'CONSTANCIA_PAPEL_FOTO'
  | 'FACHADA_FOTO'
  | 'EVIDENCIA_FOTO';

export interface Geo {
  lat: number;
  lng: number;
  precisionMetros: number;
  proveedor: string;
  esSimulada: boolean;
  capturadaEn: string;
}

export interface OrdenVisitaDTO {
  ordenId: string;
  tipoControl: TipoControl;
  programadaPara: string;
  domicilio: string;
  estado: EstadoOrden;
  empresa: { empresaId: string; nombre: string };
  trabajador: {
    trabajadorId: string;
    dni: string;
    nombreCompleto: string;
    legajo: string;
    email: string | null;
  };
  /** Estado derivado para la agenda del médico. */
  estadoVisita:
    | 'PENDIENTE'
    | 'EN_CURSO'
    | 'FIRMA_PENDIENTE'
    | 'FIRMADA'
    | 'NO_REALIZADA';
  constanciaCodigo: string | null;
  resultado: ResultadoControl | null;
}

/** Métodos de firma del médico. Todos son firma electrónica simple (Ley 25.506 art. 5). */
export type MetodoFirma = 'OTP_SANDBOX' | 'OTP_EMAIL' | 'MATRICULA_CLAVE';

export interface ChallengeFirmaDTO {
  challengeId: string;
  metodo: MetodoFirma;
  expiraEn: string;
  intentosRestantes: number;
  sujeto: 'CONSTANCIA' | 'ACTA_CONTINGENCIA' | 'DICTAMEN_JUNTA';
  descripcion: string;
}

export interface ConstanciaCreadaDTO {
  controlId: string;
  codigo: string;
  estado: EstadoConstancia;
  challenge: ChallengeFirmaDTO | null;
  /** true cuando el control fue en papel y falta firmar el acta de contingencia (HU-10). */
  actaRequerida?: boolean;
  motivoBloqueo?: string;
}

/** Item de la cola local (outbox) en el dispositivo del médico. */
export interface ItemSalidaLocal {
  id?: number;
  ordenId: string;
  idempotencyKey: string;
  tipo: 'CONTROL' | 'CONTROL_PAPEL' | 'EXCEPCION';
  payload: unknown;
  adjuntosLocales: string[];
  estado: 'ESPERANDO_RED' | 'SUBIENDO' | 'SINCRONIZADO' | 'ERROR';
  creadoEn: string;
  resumen: string;
  contingencia: boolean;
  error?: string;
  challengeId?: string;
  constanciaCodigo?: string;
}

export interface EvidenciaConectividad {
  estadoRed: 'SIN_SERVICIO' | 'SIN_DATOS' | 'TIMEOUT_BACKEND' | 'SIMULADA_DEMO';
  intentosFallidos: { en: string; error: string }[];
}
